<template>
    <div
        class="mvt-cardproperty-pagination"
    >
    <div :style="{ transform: `translateX(-${(visiblePages.length * dotSpacing) / 2}px)` }">
        <transition-group
            name="dot"
            tag="ul"
            class="dots"
        >
            <li
                v-for="(page, idx) in visiblePages"
                :key="page"
                class="dot"
                :class="[
          { active: page === currentIndex },
          ...edgeClass(idx)
        ]"
                :style="{ left: `${idx * dotSpacing}px` }"
            ></li>
        </transition-group>
    </div>
    </div>
</template>

<script>
export default {
    name: 'InstagramPagination',
    props: {
        total:     { type: Number, required: true },
        threshold: { type: Number, default: 5 },
        dotWindow: { type: Number, default: 3 },
        currentIndex: { type: Number, default: 0 },
    },
    data() {
        return {
            dotSpacing: 16  // 6px size + 8px total margin
        };
    },
    computed: {
        startIndex() {
            const half = Math.floor(this.dotWindow / 2);
            let s = this.currentIndex - half;
            s = Math.max(0, s);
            s = Math.min(s, this.total - this.dotWindow);
            return s ;
        },
        visiblePages() {
            if (this.total <= this.threshold) {
                return Array.from({ length: this.total }, (_, i) => i);
            }
            const s = this.startIndex;
            const windowIndices = Array.from(
                { length: this.dotWindow },
                (_, i) => s + i
            );
            const before = [s - 2, s - 1].filter(i => i >= 0);
            const after = [s + this.dotWindow, s + this.dotWindow + 1]
                .filter(i => i < this.total);
            return [...before, ...windowIndices, ...after];
        },
    },
    methods: {
        edgeClass(idx) {
            if (this.total <= this.threshold) return [];
            const len = this.visiblePages.length;
            const leftPages  = this.startIndex;
            const rightPages = this.total - (this.startIndex + this.dotWindow);
            const classes = [];
            if (idx === 0 && leftPages > 0)        classes.push('edge75-left');
            if (idx === 1 && leftPages > 1)        classes.push('edge85-left');
            if (idx === len - 2 && rightPages > 1) classes.push('edge85-right');
            if (idx === len - 1 && rightPages > 0) classes.push('edge75-right');
            return classes;
        }
    }
};
</script>

<style lang="scss">

.mvt-cardproperty-pagination {
    pointer-events: none;
    background: linear-gradient(to top, rgba(33, 33, 33, 0.7), transparent);
    position: absolute;
    bottom: 0;
    width: 100%;
    height: 40px;
    >div{
        display: flex;
        justify-content: center;
        padding: var(--g2);
        position: absolute;
        transform: translateX(-50%);
        width: 100%;
        bottom: 0;
    }


    .dots {
        position: relative;
        list-style: none;
        padding: 0;
        margin: 0 16px;
        height: var(--base-size);
    }

    /* animate left-position changes */
    .dot-move {
        transition: left 0.5s ease-out;
    }

    /* overall enter/leave transition */
    .dot-enter-active,
    .dot-leave-active {
        transition: opacity 0.5s ease, transform 0.5s ease;
    }

    /* default “pop” for center dots */
    .dot-enter-from,
    .dot-leave-to {
        opacity: 0;
        transform: scale(0.5);
    }

    /* left-edge dots enter/leave from the left */
    .dot.edge75-left.dot-enter-from,
    .dot.edge85-left.dot-enter-from {
        opacity: 0;
        transform: translateX(-10px) scale(var(--edge-scale));
    }
    .dot.edge75-left.dot-leave-to,
    .dot.edge85-left.dot-leave-to {
        opacity: 0;
        transform: translateX(-10px) scale(var(--edge-scale));
    }

    /* right-edge dots enter/leave from the right */
    .dot.edge75-right.dot-enter-from,
    .dot.edge85-right.dot-enter-from {
        opacity: 0;
        transform: translateX(10px) scale(var(--edge-scale));
    }
    .dot.edge75-right.dot-leave-to,
    .dot.edge85-right.dot-leave-to {
        opacity: 0;
        transform: translateX(10px) scale(var(--edge-scale));
    }

    /* Base dot styling */
    .dot {
        --base-size: 6px;
        position: absolute;
        top: 0;
        width:  var(--base-size);
        height: var(--base-size);
        border-radius: 3px;
        background-color: rgba(255,255,255, 0.5);
        transition: background-color 0.3s ease, transform 0.5s ease, width 0.3s ease, margin-left 0.3s ease;
        transform-origin: 50% 50%;
    }

    /* edge scaling via CSS variable */
    .edge85-left,
    .edge85-right {
        --edge-scale: 0.85;
        transform: scale(0.85);
    }
    .edge75-left,
    .edge75-right {
        --edge-scale: 0.75;
        transform: scale(0.75);
    }

    /* active dot */
    .dot.active {
        background-color: rgba(255,255,255, 1);
        width: 16px;
        margin-left: -5px;
    }

}

</style>
